/* import __COLOCATED_TEMPLATE__ from './main.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Tab } from 'embercom/components/standalone/common/tab-control';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'ember-intl/services/intl';
import type RouterService from '@ember/routing/router-service';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import { isPresent, isBlank } from '@ember/utils';
import { taskFor } from 'ember-concurrency-ts';

export interface Args {
  activeStep: 'connect' | 'data' | 'configure-emails' | 'workflow' | 'assign' | 'go-live';
  openSection: string | null;
  setSection?: (section: string | null) => void;
  errorCode?: string;
}

const STEPS = [
  {
    label: (self: Main) => self.intl.t('standalone.deploy.salesforce.cases.tabs.connect'),
    value: 'connect',
    path: 'apps.app.standalone.deploy.salesforce.cases.connect',
    show: (_: Main) => true,
    isCompleted: (self: Main) => self.isConnectStepComplete,
  },
  {
    label: (self: Main) => self.intl.t('standalone.deploy.salesforce.cases.tabs.configure-emails'),
    value: 'configure-emails',
    path: 'apps.app.standalone.deploy.salesforce.cases.configure-emails',
    show: (_: Main) => true,
    isCompleted: (self: Main) => self.isConfigureEmailsStepComplete,
  },
  {
    label: (self: Main) => self.intl.t('standalone.deploy.salesforce.cases.tabs.data'),
    value: 'data',
    path: 'apps.app.standalone.deploy.salesforce.cases.data',
    show: (_: Main) => true,
    isCompleted: (self: Main) =>
      self.salesforceConfig?.isEnabled ||
      (self.salesforceConfig?.isConnected &&
        self.isSelectedAfter('data') &&
        self.areBeforeStepsComplete('data')),
  },
  {
    label: (self: Main) => self.intl.t('standalone.deploy.salesforce.cases.tabs.workflow'),
    value: 'workflow',
    path: 'apps.app.standalone.deploy.salesforce.cases.workflow',
    show: (_: Main) => true,
    isCompleted: (self: Main) => self.isWorkflowStepComplete,
  },
  {
    label: (self: Main) => self.intl.t('standalone.deploy.salesforce.cases.tabs.assign'),
    value: 'assign',
    path: 'apps.app.standalone.deploy.salesforce.cases.assign',
    show: (_: Main) => true,
    isCompleted: (self: Main) =>
      self.salesforceConfig?.isEnabled ||
      (self.salesforceConfig?.isConnected &&
        self.isSelectedAfter('assign') &&
        self.areBeforeStepsComplete('assign')),
  },
  {
    label: (self: Main) => self.intl.t('standalone.deploy.salesforce.cases.tabs.go-live'),
    value: 'go-live',
    path: 'apps.app.standalone.deploy.salesforce.cases.go-live',
    show: (_: Main) => true,
    isCompleted: (self: Main) => self.salesforceConfig?.isEnabled || false,
  },
];

export default class Main extends Component<Args> {
  @service declare appService: AppService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @tracked selectedStep: Tab;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.selectedStep =
      this.deploySteps.find((step) => step.value === args.activeStep) ?? this.deploySteps[0];
  }

  get salesforceConfig(): SalesforceConfiguration | undefined {
    return this.finStandaloneService.salesforcePlatform.salesforceConfig;
  }

  get salesforceSetupData() {
    return this.finStandaloneService.salesforcePlatform.salesforceSetupData;
  }

  get isConnectStepComplete() {
    return (
      this.salesforceConfig?.isEnabled ||
      (this.salesforceConfig?.isSuccessfullyConnected &&
        isPresent(this.salesforceConfig?.finSalesforceUserId) &&
        this.salesforceSetupData.hasPermissionSet &&
        this.salesforceSetupData.hasPermissionSetAssignedToInstaller)
    );
  }

  get isConfigureEmailsStepComplete() {
    if (this.salesforceConfig?.isEnabled) {
      return true;
    }

    if (!this.salesforceConfig?.isConnected) {
      return false;
    }

    if (
      this.appService.app.hasOptionalConfigureEmails &&
      isBlank(this.finStandaloneService.salesforcePlatform.selectedSalesforceCaseOrigins)
    ) {
      return (
        this.isSelectedAfter('configure-emails') && this.areBeforeStepsComplete('configure-emails')
      );
    }

    return this.salesforceConfig.hasValidEmailConfiguration;
  }

  get visualBuilderObject() {
    return this.salesforceConfig?.ruleset.value?.rulesetLinks.firstObject.object
      .visualBuilderObject;
  }

  get isWorkflowStepComplete() {
    return (
      this.salesforceConfig?.isEnabled ||
      (this.salesforceConfig?.isConnected && this.visualBuilderObject?.validations.isValid)
    );
  }

  get deploySteps(): Array<Tab> {
    return STEPS.filter((step) => step.show(this)).map((step, index) => {
      return {
        stepNumber: index + 1,
        value: step.value,
        label: step.label(this),
        path: step.path,
        isCompleted: step.isCompleted(this),
      };
    });
  }

  isSelectedAfter(value: Tab['value']): boolean {
    if (!this.selectedStep) {
      return false;
    }

    let values = STEPS.map((step) => step.value);
    return values.indexOf(this.selectedStep.value) > values.indexOf(value);
  }

  areBeforeStepsComplete(value: Tab['value']): boolean {
    let values = STEPS.map((step) => step.value);
    let index = values.indexOf(value);
    return STEPS.slice(0, index).every((step) => step.isCompleted(this));
  }

  @action
  async handleVisibilityChange() {
    await this.maybeCheckPermissions();
    await this.maybeLoadSalesforceSupportEmails();
  }

  @action
  async maybeCheckPermissions() {
    if (
      document.hidden ||
      (this.salesforceSetupData.hasPermissionSet &&
        this.salesforceSetupData.hasPermissionSetAssignedToInstaller)
    ) {
      return;
    }

    await taskFor(
      this.finStandaloneService.salesforcePlatform.salesforceDataLoader.checkSalesforcePermissions,
    ).perform();
  }

  @action
  async maybeLoadSalesforceSupportEmails() {
    if (document.hidden || isPresent(this.salesforceSetupData.supportEmails)) {
      return;
    }

    await taskFor(
      this.finStandaloneService.salesforcePlatform.salesforceDataLoader.loadSalesforceSupportEmails,
    ).perform();
  }

  @action
  setStep(stepValue: string) {
    let step = this.deploySteps.find((step) => step.value === stepValue);
    if (step) {
      this.selectedStep = step;
      if (step.path) {
        this.router.transitionTo(step.path);
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Deploy::Salesforce::Cases::Main': typeof Main;
    'standalone/deploy/salesforce/cases/main': typeof Main;
  }
}
